import React from 'react';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  RadioGroup,
  TextField,
  Drawer,
  InputLabel,
  Input,
  InputAdornment,
  Checkbox,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import CloseIcon from '@mui/icons-material/Close';
import { ApplicationFilterType } from '../../redux-store/types/api-types';
import { filterApplicationTypes, ModeOfHoldingTypes, USER_ROLES } from '../../utils/constant';
import { Distributor } from '../../redux-store/actions';
import { LocalizationProvider, MobileDatePicker, PickersDay } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import KeyboardArrowRightSharpIcon from '@mui/icons-material/KeyboardArrowRightSharp';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';

type FiltersDropDownProps = {
  filters: ApplicationFilterType;
  anchorEl: boolean;
  onFiltersChange: (value: ApplicationFilterType) => void;
  handleClose: () => void;
  distributors: Distributor[];
  loader?: boolean;
};

export const FilterDatePicker = ({
  value,
  onChange,
  placeholder = '',
  errorText = '',
  id = 'date-picker',
  ariaLabel,
  ariaLabelledBy,
}: {
  value: Date;
  onChange: (date: Date | null) => void;
  placeholder: string;
  errorText?: string;
  id?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}): JSX.Element => {
  const resolvedAriaLabel =
    ariaLabel ||
    (placeholder.toLowerCase().includes('from')
      ? 'From Date'
      : placeholder.toLowerCase().includes('to')
      ? 'To Date'
      : placeholder);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl variant="outlined" fullWidth>
        <MobileDatePicker
          readOnly={false}
          components={{ OpenPickerIcon: KeyboardArrowRightSharpIcon }}
          value={value}
          maxDate={new Date()}
          inputFormat="dd/MM/yyyy"
          onChange={onChange}
          DialogProps={{
            PaperProps: {
              'aria-label': resolvedAriaLabel,
              'aria-labelledby': undefined,
            },
          }}
          renderDay={(_day, _selectedDays, pickersDayProps) => (
            <PickersDay
              {...pickersDayProps}
              aria-current={pickersDayProps.selected ? 'true' : undefined}
            />
          )}
          renderInput={(params) => (
            <TextField
              fullWidth
              id={id}
              placeholder={placeholder}
              {...params}
              InputProps={{ ...params.InputProps, readOnly: false }}
              inputProps={{
                ...params.inputProps,
                readOnly: false,
                'aria-readonly': undefined,
                autoComplete: 'off',
                'aria-label': resolvedAriaLabel,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  height: '44px',
                },
              }}
            />
          )}
          disableCloseOnSelect={false}
          showToolbar={false}
        />
        {errorText && (
          <FormHelperText error sx={{ marginLeft: 'unset' }}>
            {errorText}
          </FormHelperText>
        )}
      </FormControl>
    </LocalizationProvider>
  );
};

const Inival = (role: string) => {
  return {
    applicationType: [],
    modeOfHolding: [],
    status: [],
    distributorId: [],
    applicationSentBack: null,
  };
};

export const initialFilters = (
  role: string,
  storeStatus: string,
  storeApplicationType: string,
  storeModeOfHolding: string,
  storeDistributorId: string
): ApplicationFilterType => {
  return {
    applicationType: storeApplicationType ? storeApplicationType.split(',') : [],
    modeOfHolding: storeModeOfHolding ? storeModeOfHolding.split(',') : [],
    status: storeStatus ? storeStatus.split(',') : [],
    distributorId: storeDistributorId ? storeDistributorId.split(',') : [],
  };
};

const SearchComponent = ({
  search,
  setSearch,
}: {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  return (
    <FormControl variant="standard" sx={{ display: 'block' }}>
      <InputLabel
        htmlFor="input-with-icon-adornment"
        sx={{ '&.Mui-focused': { color: 'text.primary' } }}>
        Search
      </InputLabel>
      <Input
        id="input-with-icon-adornment"
        value={search}
        autoComplete={'off'}
        onChange={({ target: { value } }) => setSearch(value)}
        endAdornment={
          <InputAdornment position="end">
            <IconButton aria-label="Search">
              <SearchSharpIcon sx={{ color: 'primary.main' }} aria-hidden="true" />
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  );
};

export const FilterDropdown = ({
  filters,
  anchorEl,
  onFiltersChange,
  distributors = [],
  handleClose,
  loader,
}: FiltersDropDownProps): JSX.Element => {
  const [distributorSearch, setDistributorSearch] = useState('');
  const {
    status: storeStatus,
    applicationType: storeApplicationType,
    modeOfHolding: storeModeOfHolding,
    distributorId: storeDistributorId,
  } = useSelector((store: RootStateType) => store.paramsObj);
  const { role } = useSelector((store: RootStateType) => store.auth);
  const [modalState, setModalState] = useState(
    initialFilters(role, storeStatus, storeApplicationType, storeModeOfHolding, storeDistributorId)
  );
  const [activeCategory, setActiveCategory] = useState('Status');

  useEffect(() => {
    setModalState(filters);
    setActiveCategory('Status');
  }, [filters, anchorEl]);

  const _handleModalState = () => {
    handleClose();
    setModalState(
      initialFilters(
        role,
        storeStatus,
        storeApplicationType,
        storeModeOfHolding,
        storeDistributorId
      )
    );
  };

  const _handleApplyFilters = () => {
    onFiltersChange(modalState);
    _handleModalState();
  };

  const filterApplicatiostatusTypes = [
    { key: 'draft', value: 'Draft' },
    { key: 'draft_send_back', value: 'Draft(Send Back)' },
    { key: 'sent_to_applicant1', value: 'Sent to 1st applicant' },
    { key: 'approved_by_applicant1', value: 'Approved by 1st Applicant' },
    { key: 'approved_by_applicant2', value: 'Approved by 2nd Applicant' },
    { key: 'approved_by_applicant3', value: 'Approved by 3rd Applicant' },
    { key: 'approved_by_fundmanager', value: 'Approved by investment manager' },
    { key: 'invitationexpired', value: 'Invitation Expired' },
    { key: 'completed,signed', value: 'Completed' },
    { key: 'rejected', value: 'Rejected' },
    {
      key: 'sent_to_poa_approver',
      value: role === 'poaapprover' ? 'Pending' : 'Sent to POA approver',
    },
    {
      key: 'sent_to_amc_approver',
      value: role === 'amcapprover' ? 'Pending' : 'Sent to AIF approver',
    },
    { key: 'sent_to_authorised_signatories', value: 'Sent to authorised signatories' },
    { key: 'approved_by_authorised_signatories', value: 'Approved by authorised signatories' },
  ];

  const _handleClearFilters = () => {
    onFiltersChange(Inival(role));
    handleClose();
  };

  const _handleSelection = (item: string[] | string | number, key: string | any) => {
    const index = (modalState[key] as string[]).indexOf(item as string);
    if (index === -1) {
      setModalState({
        ...modalState,
        [key]: [...((modalState[key] as string[]) || []), item] as string[],
      });
    } else {
      setModalState({
        ...modalState,
        [key]: (modalState[key] as string[]).filter((status: any) => status != (item as string)),
      });
    }
  };

  const filterCategories = [
    'Status',
    ...([USER_ROLES.AMCAPPROVER, USER_ROLES.AMC_ADMIN].includes(role) ? ['Distributor Name'] : []),
    'Mode of Holding',
    'Investor Type',
  ];

  const checkboxSx = {
    ml: 0,
    mb: 0.5,
    mr: 0,
    width: '100%',
    alignItems: 'center',
    '.MuiTypography-root': {
      fontFamily: 'Inter, sans-serif',
      color: '#111927',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1,
      letterSpacing: 0,
    },
    '.MuiCheckbox-root': { p: 0.5, mr: 1, color: '#9CA3AF' },
    '.Mui-checked': { color: 'primary.main' },
  };

  const renderCategoryOptions = () => {
    if (activeCategory === 'Status') {
      return (
        <RadioGroup aria-label="status" defaultValue="" name="status">
          {filterApplicatiostatusTypes
            .filter((obj) => {
              if (role === 'amcapprover') {
                return (
                  !['draft', 'draft_send_back'].includes(obj.key) &&
                  obj.key !== 'sent_to_poa_approver'
                );
              }
              if (role === 'poaapprover') {
                return !['draft', 'draft_send_back'].includes(obj.key);
              }
              return obj;
            })
            .map((type: { key: string; value: string }) => (
              <FormControlLabel
                key={type.key}
                value={type.key}
                onChange={() => _handleSelection(type.key, 'status')}
                control={<Checkbox size="small" checked={modalState.status.includes(type.key)} />}
                label={type.value}
                title={type.value}
                sx={checkboxSx}
              />
            ))}
        </RadioGroup>
      );
    }

    if (activeCategory === 'Distributor Name') {
      const filteredDistributors = distributorSearch
        ? distributors.filter((distributor) =>
            distributor.name.trim().toLowerCase().includes(distributorSearch.trim().toLowerCase())
          )
        : distributors;
      return (
        <>
          <SearchComponent search={distributorSearch} setSearch={setDistributorSearch} />
          {loader ? (
            <Typography sx={{ mt: 2, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
              Loading...
            </Typography>
          ) : filteredDistributors.length ? (
            <Box sx={{ mt: 2 }}>
              <RadioGroup aria-label="Distributor Name" defaultValue="" name="Distributor Name">
                {filteredDistributors.map((distributor: Distributor) => (
                  <FormControlLabel
                    key={distributor.id}
                    value={distributor.id.toString()}
                    onChange={() => _handleSelection(distributor.id, 'distributorId')}
                    control={
                      <Checkbox
                        size="small"
                        checked={modalState.distributorId
                          .map((dis) => dis.toString())
                          .includes(distributor.id.toString())}
                      />
                    }
                    label={distributor.name}
                    title={distributor.name}
                    sx={checkboxSx}
                  />
                ))}
              </RadioGroup>
            </Box>
          ) : (
            <Typography sx={{ mt: 2, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
              No Results found
            </Typography>
          )}
        </>
      );
    }

    if (activeCategory === 'Mode of Holding') {
      return (
        <RadioGroup aria-label="modeOfHolding" defaultValue="" name="modeOfHolding">
          {ModeOfHoldingTypes.map((type: { key: string; value: string }) => (
            <FormControlLabel
              key={type.key}
              value={type.key}
              onChange={() => _handleSelection(type.key, 'modeOfHolding')}
              control={
                <Checkbox size="small" checked={modalState.modeOfHolding.includes(type.key)} />
              }
              label={type.value}
              title={type.value}
              sx={checkboxSx}
            />
          ))}
        </RadioGroup>
      );
    }

    if (activeCategory === 'Investor Type') {
      return (
        <RadioGroup aria-label="applicationType" defaultValue="" name="applicationType">
          {filterApplicationTypes.map((type: { key: string; value: string }) => (
            <FormControlLabel
              key={type.key}
              value={type.key}
              onChange={() => _handleSelection(type.key, 'applicationType')}
              control={
                <Checkbox size="small" checked={modalState.applicationType.includes(type.key)} />
              }
              label={type.value}
              title={type.value}
              sx={checkboxSx}
            />
          ))}
        </RadioGroup>
      );
    }

    return null;
  };

  return (
    <Box>
      <Drawer
        anchor="right"
        open={anchorEl}
        onClose={_handleModalState}
        ModalProps={{
          id: 'filter-menu',
          role: 'dialog',
          'aria-label': 'Filter',
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520 },
            maxWidth: '100%',
            borderRadius: { xs: 0, sm: '16px 0 0 16px' },
            fontFamily: 'Inter, sans-serif',
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            bgcolor: '#FFFFFF',
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: '1px solid #E5E7EB',
            }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 18,
                fontWeight: 700,
                color: '#111927',
                lineHeight: 1,
                letterSpacing: 0,
              }}>
              Filter
            </Typography>
            <IconButton aria-label="Close" onClick={_handleModalState} sx={{ p: 0.5 }}>
              <CloseIcon sx={{ color: '#6B7280', fontSize: 22 }} aria-hidden="true" />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}>
            <Box
              component="nav"
              aria-label="Filter categories"
              sx={{
                width: 180,
                flexShrink: 0,
                borderRight: '1px solid #E5E7EB',
                py: 1,
                overflowY: 'auto',
              }}>
              {filterCategories.map((category) => {
                const isActive = activeCategory === category;
                return (
                  <Box
                    key={category}
                    component="button"
                    type="button"
                    onClick={() => setActiveCategory(category)}
                    aria-current={isActive ? 'true' : undefined}
                    sx={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      font: 'inherit',
                      position: 'relative',
                      px: 2.5,
                      py: 1.5,
                      color: isActive ? 'primary.main' : '#111927',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 16,
                      fontWeight: isActive ? 600 : 500,
                      lineHeight: 1,
                      letterSpacing: 0,
                      '&::before': isActive
                        ? {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 8,
                            bottom: 8,
                            width: 3,
                            borderRadius: '0 2px 2px 0',
                            bgcolor: 'primary.main',
                          }
                        : undefined,
                      '&:hover': {
                        bgcolor: '#F9FAFB',
                      },
                      '&:focus-visible': {
                        outline: '3px solid #2057A6',
                        outlineOffset: '-3px',
                      },
                    }}>
                    {category}
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: 2.5,
                overflowY: 'auto',
              }}>
              {renderCategoryOptions()}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderTop: '1px solid #E5E7EB',
            }}>
            <Button
              type="reset"
              onClick={_handleClearFilters}
              sx={{
                color: 'primary.main',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                lineHeight: 1,
                letterSpacing: 0,
                textTransform: 'none',
                p: 0,
                minWidth: 'unset',
                '&:hover': {
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                },
              }}>
              Clear filters
            </Button>
            <Button
              type="submit"
              variant="contained"
              onClick={_handleApplyFilters}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                height: 'auto',
                minWidth: 'unset',
                bgcolor: 'primary.main',
                color: '#FFFFFF',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: 14,
                lineHeight: 1,
                letterSpacing: 0,
                textTransform: 'none',
                borderRadius: '8px',
                pt: '9px',
                pr: '28px',
                pb: '9px',
                pl: '28px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: 'none',
                },
              }}>
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};
